import React, { Fragment, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { boardTemplateService, BoardTemplate, TemplateType, TEMPLATE_TYPES } from "../../../../lib/api/boardTemplates";
import { useAlertStore } from "../../../../stores/alertStore";
import { Spinner } from "../../../../components/LogoLoader";

interface BoardTemplatesModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApplyTemplate: (config: any[]) => void;
}

const CATEGORIES: (TemplateType | 'All')[] = ['All', ...TEMPLATE_TYPES];

export const BoardTemplatesModal: React.FC<BoardTemplatesModalProps> = ({
    isOpen,
    onClose,
    onApplyTemplate,
}) => {
    const { t } = useTranslation("common");
    const { showAlert } = useAlertStore();
    const [templates, setTemplates] = useState<BoardTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<TemplateType | 'All'>('All');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // Fetch templates when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchTemplates();
        }
    }, [isOpen]);

    const fetchTemplates = async () => {
        setIsLoading(true);
        try {
            const data = await boardTemplateService.getTemplates();
            setTemplates(data);
        } catch (error) {
            console.error("Error fetching templates:", error);
            showAlert({
                type: "error",
                title: String(t("errors.error")),
                message: String(t("userErrors.boards.unableToLoad")),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const filteredTemplates = useMemo(() => {
        if (selectedCategory === 'All') return templates;
        return templates.filter(template => template.templateType === selectedCategory);
    }, [templates, selectedCategory]);

    const handleDeleteTemplate = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        try {
            await boardTemplateService.deleteTemplate(id);
            setTemplates(prev => prev.filter(t => t.id !== id));
        } catch (error) {
            console.error("Error deleting template:", error);
            showAlert({ type: "error", title: String(t("errors.error")), message: String(t("userErrors.boards.unableToLoad")) });
        }
    };

    const handleExportTemplate = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        try {
            await boardTemplateService.exportTemplate(id);
        } catch (error) {
            console.error("Error exporting template:", error);
            showAlert({ type: "error", title: String(t("errors.error")), message: String(t("userErrors.boards.unableToLoad")) });
        }
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const template = await boardTemplateService.importTemplate(file);
            setTemplates(prev => [template, ...prev]);

        } catch (error) {
            console.error("Error importing template:", error);
            showAlert({ type: "error", title: String(t("errors.error")), message: String(t("userErrors.boards.unableToLoad")) });
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            try {
                const template = await boardTemplateService.importTemplate(file);
                setTemplates(prev => [template, ...prev]);

            } catch (error) {
                console.error("Error importing template:", error);
                showAlert({ type: "error", title: String(t("errors.error")), message: String(t("userErrors.boards.unableToLoad")) });
            }
        }
    };

    // Map template type to translation key
    const getTypeKey = (type: string) => {
        const mapping: Record<string, string> = {
            'All': 'all',
            'Financial Insights': 'financialInsights',
            'Decision Support': 'decisionSupport',
            'Operational Efficiency': 'operationalEfficiency',
            'Customer Behavior': 'customerBehavior',
            'Predictive Analytics': 'predictiveAnalytics',
            'Risk Management': 'riskManagement',
            'Other': 'other'
        };
        return mapping[type] || 'other';
    };

    // Generate a color based on index for preview
    const getPreviewColor = (i: number) => {
        const colors = [
            "bg-blue-50 border-blue-100 text-blue-500",
            "bg-emerald-50 border-emerald-100 text-emerald-500",
            "bg-violet-50 border-violet-100 text-violet-500",
            "bg-amber-50 border-amber-100 text-amber-500",
            "bg-rose-50 border-rose-100 text-rose-500",
            "bg-indigo-50 border-indigo-100 text-indigo-500"
        ];
        return colors[i % colors.length];
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 modal-backdrop transition-opacity duration-200" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-6xl transform transition-all duration-200">
                                <div className="bg-neutral-50 dark:bg-neutral-900 rounded-2xl shadow-2xl h-[90vh] sm:h-[85vh] flex flex-col overflow-hidden border border-white/20 dark:border-white/10">
                                    {/* Header */}
                                    <div className="px-5 sm:px-8 pt-6 pb-4 sm:pb-6 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex flex-col gap-5 sm:gap-6 shrink-0">
                                        <div className="flex justify-between items-start">
                                            <div className="pr-4">
                                                <Dialog.Title as="h2" className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
                                                    {t("templates.title")}
                                                </Dialog.Title>
                                                <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-1 line-clamp-1 sm:line-clamp-none">
                                                    {t("templates.subtitle")}
                                                </p>
                                            </div>
                                            <button
                                                onClick={onClose}
                                                className="text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300 p-2 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all duration-200 shrink-0"
                                            >
                                                <span className="w-5 h-5 sm:w-6 h-6" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_37002b9f.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_37002b9f.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            </button>
                                        </div>

                                        {/* Tabs Container - Modern Segmented Control */}
                                        <div className="relative">
                                            <div className="flex items-center overflow-x-auto scrollbar-hide scroll-smooth -mx-5 px-5 sm:mx-0 sm:px-0">
                                                <div className="flex p-1 bg-neutral-100/80 dark:bg-neutral-800/80 rounded-2xl border border-neutral-200/50 dark:border-neutral-700/50 backdrop-blur-sm min-w-max">
                                                    {CATEGORIES.map((cat) => (
                                                        <button
                                                            key={cat}
                                                            onClick={() => setSelectedCategory(cat)}
                                                            className={`px-4 sm:px-5 py-2 rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition-all duration-300 relative ${selectedCategory === cat
                                                                ? 'bg-white dark:bg-neutral-700 text-accent-primary shadow-sm shadow-accent-primary/10 ring-1 ring-black/[0.02]'
                                                                : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-white/40 dark:hover:bg-white/10'
                                                                }`}
                                                        >
                                                            {t(`templates.categories.${getTypeKey(cat)}`)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 overflow-y-auto custom-scrollbar bg-neutral-50/30 dark:bg-neutral-950/30 p-4">
                                        {isLoading ? (
                                            <div className="flex items-center justify-center h-full">
                                                <Spinner size="md" className="text-accent-primary" />
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                                {/* Empty Page Option */}
                                                <div
                                                    onClick={() => {
                                                        onApplyTemplate([]);
                                                        onClose();
                                                    }}
                                                    className="group relative flex flex-col bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-2xl cursor-pointer hover:border-accent-primary dark:hover:border-accent-primary hover:shadow-xl hover:shadow-accent-primary/5 transition-all duration-300 overflow-hidden h-44"
                                                >
                                                    <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-800 dark:to-neutral-900 group-hover:from-accent-primary/5 group-hover:to-accent-primary/10 transition-colors duration-300">
                                                        <div className="p-4 rounded-full bg-white dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 text-neutral-400 group-hover:text-accent-primary group-hover:border-accent-primary/30 transition-all duration-300 shadow-sm">
                                                            <span className="w-8 h-8" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_3af6d363.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3af6d363.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                        </div>
                                                    </div>
                                                    <div className="p-4 bg-white dark:bg-neutral-800 border-t border-neutral-100 dark:border-neutral-700">
                                                        <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 group-hover:text-accent-primary transition-colors duration-200 truncate">
                                                            {t("templates.emptyPage")}
                                                        </h3>
                                                        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                                                            {t("templates.emptyPageDescription")}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Template Items */}
                                                {filteredTemplates.map((template, idx) => (
                                                    <div
                                                        key={template.id}
                                                        onClick={() => {
                                                            onApplyTemplate(template.chartConfig);
                                                            onClose();
                                                        }}
                                                        className="group relative flex flex-col bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-2xl cursor-pointer hover:border-accent-primary dark:hover:border-accent-primary hover:shadow-xl hover:shadow-accent-primary/5 transition-all duration-300 overflow-hidden h-44"
                                                    >
                                                        {/* Preview Area */}
                                                        <div className={`flex-1 relative overflow-hidden border-b transition-colors duration-300 ${getPreviewColor(idx)} group-hover:bg-opacity-80`}>
                                                            <div className="w-full h-full p-2 relative">
                                                                {Array.isArray(template.chartConfig) && template.chartConfig.length > 0 ? (
                                                                    template.chartConfig.map((item: any, itemIdx: number) => {
                                                                        const maxRows = 6;
                                                                        const wPercent = Math.min((item.w / 12) * 100, 100);
                                                                        const xPercent = (item.x / 12) * 100;
                                                                        const hPercent = Math.min((item.h / maxRows) * 100, 100);
                                                                        const yPercent = (item.y / maxRows) * 100;
                                                                        if (item.y >= maxRows) return null;

                                                                        return (
                                                                            <div
                                                                                key={`preview-${template.id}-${itemIdx}`}
                                                                                className="absolute bg-white/80 dark:bg-neutral-600/80 border border-black/5 dark:border-white/5 rounded-[3px] shadow-sm transform transition-transform group-hover:scale-[1.02]"
                                                                                style={{
                                                                                    left: `${xPercent}%`,
                                                                                    top: `${yPercent}%`,
                                                                                    width: `${wPercent}%`,
                                                                                    height: `${hPercent}%`,
                                                                                }}
                                                                            />
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center">
                                                                        <span className="w-8 h-8 opacity-30" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_0f07ab16.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_0f07ab16.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Actions (Export/Delete) */}
                                                            <div className="absolute top-3 right-3 flex gap-1 transition-all duration-300">
                                                                <button
                                                                    onClick={(e) => handleExportTemplate(e, template.id)}
                                                                    className="p-1.5 bg-white dark:bg-neutral-800 backdrop-blur-md rounded-lg shadow-lg text-neutral-500 dark:text-neutral-400 hover:text-accent-primary dark:hover:text-accent-primary transition-colors"
                                                                    title={t("actions.export") || "Export"}
                                                                >
                                                                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_548d26bf.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_548d26bf.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                </button>
                                                                <button
                                                                    onClick={(e) => handleDeleteTemplate(e, template.id)}
                                                                    className="p-1.5 bg-white dark:bg-neutral-800 backdrop-blur-md rounded-lg shadow-lg text-neutral-500 dark:text-neutral-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                                                                    title={t("actions.delete") || "Delete"}
                                                                >
                                                                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_c66677ac.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_c66677ac.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Content */}
                                                        <div className="p-4 bg-white dark:bg-neutral-800">
                                                            <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 group-hover:text-accent-primary transition-colors duration-200 truncate">
                                                                {template.name}
                                                            </h3>
                                                            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                                                                {template.description || t(`templates.categories.${getTypeKey(template.templateType)}`)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Empty State */}
                                                {filteredTemplates.length === 0 && selectedCategory !== 'All' && (
                                                    <div className="col-span-full py-20 flex flex-col items-center justify-center text-neutral-400">
                                                        <span className="w-16 h-16 mb-4 opacity-20" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_873583e3.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_873583e3.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                        <p className="text-lg font-medium">{t("templates.empty.title")}</p>
                                                        <p className="text-sm">{t("templates.empty.subtitle")}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer - Import Section */}
                                    <div className="px-5 sm:px-8 py-4 sm:py-5 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shrink-0">
                                        <div
                                            onClick={handleImportClick}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            className={`w-full h-20 border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ${isDragging
                                                ? "border-accent-primary bg-accent-primary/5"
                                                : "border-neutral-300 dark:border-neutral-700 hover:border-accent-primary/50 hover:bg-accent-primary/5"
                                                }`}
                                        >
                                            <span className={`w-6 h-6 mb-1 ${isDragging ? "text-accent-primary" : "text-neutral-400"}`} style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_1814e5ae.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_1814e5ae.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            <span className="text-xs font-medium text-neutral-600">
                                                {isDragging ? t("templates.import.dropHere") : t("templates.import.clickToImport")}
                                            </span>
                                            <span className="text-[10px] text-neutral-400 mt-1">
                                                {t("templates.import.supportedFormats")}
                                            </span>
                                        </div>

                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            accept=".json"
                                            className="hidden"
                                        />
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
